"use client";

import React from "react";
import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

interface DashboardErrorBoundaryState {
  hasError: boolean;
}

/**
 * Error boundary for dashboard content. Catches uncaught errors in the main
 * content area and shows a fallback so the sidebar and shell remain usable.
 */
export class DashboardErrorBoundary extends React.Component<
  { children: React.ReactNode },
  DashboardErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): DashboardErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Dashboard error boundary caught:", error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="flex flex-col items-center justify-center gap-4 p-8 text-center text-destructive"
          role="alert"
        >
          <AlertCircle className="h-12 w-12 shrink-0" />
          <p className="font-medium">Something went wrong</p>
          <p className="text-sm text-muted-foreground">
            An unexpected error occurred. You can try again or refresh the page.
          </p>
          <Button variant="outline" size="sm" onClick={this.handleRetry}>
            Try again
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}
